import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, addDoc, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import AddHouseModal from './AddHouseModal';
import LoadingSpinner from './LoadingSpinner';

const HOUSE_STATUSES = {
  UNDERWRITING: 'Underwriting',
  UNQUALIFIED: 'Unqualified',
  UNDER_CONTRACT: 'Under Contract',
  UNDER_MANAGEMENT: 'Under Management',
  DIVESTED: 'Divested'
};

const HOUSE_STATUS_CLASSES = {
  UNDERWRITING: 'bg-primary',
  UNQUALIFIED: 'bg-secondary',
  UNDER_CONTRACT: 'bg-warning',
  UNDER_MANAGEMENT: 'bg-success',
  DIVESTED: 'bg-info'
};

const PropertyList = () => {
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [portfolioMetrics, setPortfolioMetrics] = useState({
    totalValue: 0,
    totalEquity: 0,
    totalNetIncome: 0,
    totalCashInvested: 0,
    aggregateROI: 0,
    aggregateCashOnCash: 0,
    lastMonthCashFlow: 0,
    lastMonthName: '',
    lastMonthYear: 0,
    propertyCount: 0
  });
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch properties created by the current user
        const userPropertiesQuery = query(
          collection(db, 'properties'),
          where('userId', '==', currentUser.uid)
        );
        const userPropertiesSnapshot = await getDocs(userPropertiesQuery);
        const userProperties = userPropertiesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        console.log('User properties fetched by userId:', userProperties);
        
        setProperties(userProperties);
        setFilteredProperties(userProperties);
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setIsLoading(false);
      }
    };

    fetchData();
  }, [currentUser]);

  useEffect(() => {
    // No initialization needed for CSS tooltips
  }, [properties, isLoading]);

  // Function to get properties by status
  const getPropertiesByStatus = useCallback((status) => {
    return filteredProperties.filter(property => property.status === status);
  }, [filteredProperties]);

  // Function to apply all filters (search and status)  
  const applyFilters = useCallback(() => {
    let result = [...properties];
    
    // Apply search filter if there is a search term
    if (searchTerm.trim() !== '') {
      const searchTermLower = searchTerm.toLowerCase();
      result = result.filter(property => {
        // Convert all values to strings before calling toLowerCase()
        const streetAddress = String(property.streetAddress || '').toLowerCase();
        const city = String(property.city || '').toLowerCase();
        const state = String(property.state || '').toLowerCase();
        const zipCode = String(property.zipCode || '').toLowerCase();
        
        return streetAddress.includes(searchTermLower) || 
               city.includes(searchTermLower) || 
               state.includes(searchTermLower) || 
               zipCode.includes(searchTermLower);
      });
    }
    
    // Apply status filter if not set to ALL
    if (statusFilter !== 'ALL') {
      result = result.filter(property => property.status === statusFilter);
    }
    
    setFilteredProperties(result);
  }, [properties, searchTerm, statusFilter]);
  
  // Apply filters whenever search term or status filter changes
  useEffect(() => {
    applyFilters();
  }, [applyFilters]);
  
  // Function to handle search input changes
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  
  // Function to handle status filter changes
  const handleStatusFilterChange = (status) => {
    setStatusFilter(status);
  };

  // Function to calculate portfolio metrics based on provided properties
  const calculatePortfolioMetrics = useCallback((propertiesToCalculate) => {
    // Filter for properties under management
    const managedProperties = propertiesToCalculate.filter(
      property => property.status === 'UNDER_MANAGEMENT'
    );
    
    // Calculate total portfolio value and equity
    let totalValue = 0;
    let totalEquity = 0;
    let totalCashInvested = 0;
    let totalNetIncome = 0;
    let lastMonthCashFlow = 0;
    
    // Get the last full month (e.g., if it's March 16, 2025, we want February 2025)
    const currentDate = new Date();
    const lastMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
    const lastMonthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0);
    
    // Format for logging
    const lastMonthName = lastMonth.toLocaleString('default', { month: 'long' });
    const lastMonthYear = lastMonth.getFullYear();
    
    // Fetch all transactions for the last month
    const fetchLastMonthTransactions = async () => {
      try {
        // For each property, get transactions from the last month
        for (const property of managedProperties) {
          if (!property.id) continue;
          
          const transactionsQuery = query(
            collection(db, 'transactions'),
            where('propertyId', '==', property.id),
            where('date', '>=', lastMonth.toISOString().split('T')[0]),
            where('date', '<=', lastMonthEnd.toISOString().split('T')[0])
          );
          
          const transactionsSnapshot = await getDocs(transactionsQuery);
          let propertyMonthlyIncome = 0;
          let propertyMonthlyExpense = 0;
          
          transactionsSnapshot.docs.forEach(doc => {
            const transaction = doc.data();
            if (transaction.type === 'income') {
              propertyMonthlyIncome += Number(transaction.amount || 0);
            } else if (transaction.type === 'expense') {
              propertyMonthlyExpense += Number(transaction.amount || 0);
            }
          });
          
          // Calculate net cash flow for this property for the last month
          const propertyLastMonthCashFlow = propertyMonthlyIncome - propertyMonthlyExpense;
          lastMonthCashFlow += propertyLastMonthCashFlow;
        }
        
        // Update portfolio metrics with the last month's cash flow
        setPortfolioMetrics(prev => ({
          ...prev,
          lastMonthCashFlow,
          lastMonthName,
          lastMonthYear
        }));
      } catch (error) {
        console.error('Error fetching last month transactions:', error);
      }
    };
    
    managedProperties.forEach(property => {
      // Use currentValue if available, otherwise use purchasePrice as fallback
      const propertyValue = property.currentValue || property.purchasePrice || 0;
      totalValue += Number(propertyValue);
      
      // Calculate equity: currentValue - mortgageBalance
      const mortgageBalance = property.mortgageBalance || 0;
      const equity = Number(propertyValue) - Number(mortgageBalance);
      totalEquity += equity > 0 ? equity : 0; // Ensure we don't add negative equity
      
      // Add up total cash invested for ROI calculations
      totalCashInvested += Number(property.totalCashInvested || 0);
      
      // Add up total net income for ROI calculations
      totalNetIncome += Number(property.netIncome || 0);
    });
    
    // Calculate aggregate Cash on Cash
    const aggregateCashOnCash = totalCashInvested > 0 ? (totalNetIncome / totalCashInvested) * 100 : 0;
    
    // Calculate aggregate ROI (includes equity gains)
    const totalEquityGain = totalEquity - totalCashInvested;
    const aggregateROI = totalCashInvested > 0 ? 
      ((totalNetIncome + totalEquityGain) / totalCashInvested) * 100 : 0;
    
    setPortfolioMetrics({
      totalValue,
      totalEquity,
      totalNetIncome,
      totalCashInvested,
      aggregateROI,
      aggregateCashOnCash,
      lastMonthCashFlow: 0, // Will be updated by fetchLastMonthTransactions
      lastMonthName: lastMonthName,
      lastMonthYear: lastMonthYear,
      propertyCount: managedProperties.length
    });
    
    // Fetch last month's transactions
    fetchLastMonthTransactions();
  }, []);

  useEffect(() => {
    // Calculate portfolio metrics whenever properties change
    if (!isLoading) {
      calculatePortfolioMetrics(properties);
    }
  }, [properties, isLoading, calculatePortfolioMetrics]);

  const handleAddProperty = async (propertyData) => {
    try {
      // Just refresh the properties list
      const userPropertiesQuery = query(
        collection(db, 'properties'),
        where('userId', '==', currentUser.uid)
      );
      const userPropertiesSnapshot = await getDocs(userPropertiesQuery);
      const userProperties = userPropertiesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setProperties(userProperties);

      // Navigate to the new property page
      navigate(`/property/${propertyData.id}`);
    } catch (error) {
      console.error('Error refreshing properties:', error);
    }
  };

  const handleCardClick = (id) => {
    navigate(`/property/${id}`);
  };

  // Custom tooltip component
  const TooltipIcon = ({ text }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    
    return (
      <div 
        className="ms-1 text-muted position-relative d-inline-block" 
        style={{ cursor: 'help' }}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <span 
          className="d-inline-flex justify-content-center align-items-center rounded-circle bg-secondary text-white" 
          style={{ 
            width: '14px', 
            height: '14px', 
            fontSize: '10px',
            fontWeight: 'bold'
          }}
        >
          i
        </span>
        {showTooltip && (
          <div 
            className="position-absolute bg-dark text-white p-2 rounded shadow-sm" 
            style={{ 
              width: '200px', 
              zIndex: 1000, 
              bottom: '100%', 
              left: '50%', 
              transform: 'translateX(-50%)',
              fontSize: '0.8rem',
              border: '1px solid #6c757d'
            }}
          >
            {text}
          </div>
        )}
      </div>
    );
  };

  const PropertySection = ({ status, title }) => {
    const housesInSection = getPropertiesByStatus(status);
    if (housesInSection.length === 0) return null;
    
    // Check if we're on mobile
    const isMobile = window.innerWidth <= 768;
    
    return (
      <>
        <h2 className="h6 text-uppercase mb-2 mt-4" style={{ color: '#8B93FF' }}>{title}</h2>
        {isMobile ? (
          <div className="mobile-property-list">
            {housesInSection.map(property => (
              <div 
                className="mobile-property-card mb-2" 
                key={property.id}
                onClick={() => handleCardClick(property.id)}
              >
                <div className="d-flex justify-content-between align-items-center p-3" style={{ background: '#2A2A2A', borderRadius: '8px' }}>
                  <div>
                    <h5 className="mb-0 text-white" style={{ fontSize: '16px' }}>
                      {property.address.street}
                    </h5>
                    <p className="mb-0 text-muted" style={{ fontSize: '14px' }}>
                      {[
                        property.address.city,
                        property.address.state,
                        property.address.zipCode
                      ].filter(Boolean).join(', ')}
                    </p>
                  </div>
                  <div>
                    <button className="btn btn-link text-white p-0">
                      <i className="bi bi-three-dots-vertical"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 row-cols-xl-4 g-4">
            {housesInSection.map(property => (
              <div className="col" key={property.id}>
                <div 
                  className="card h-100 bg-dark border-secondary property-card hover-effect" 
                  onClick={() => handleCardClick(property.id)}
                >
                  <div className="card-body p-3">
                    <h5 className="card-title text-light mb-2">
                      {property.address.street}
                    </h5>
                    <p className="card-text text-muted mb-2">
                      {[
                        property.address.city,
                        property.address.state,
                        property.address.zipCode
                      ].filter(Boolean).join(', ')}
                    </p>
                    <div className="d-flex justify-content-between align-items-center">
                      <span className={`badge ${HOUSE_STATUS_CLASSES[status]}`}>
                        {HOUSE_STATUSES[status]}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </>
    );
  };

  // Check if we're on mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // Add event listener for window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className={`${isMobile ? 'px-0 py-0 position-relative' : 'container-fluid px-4 py-4'}`} style={isMobile ? {background: '#1f2028', minHeight: '100vh'} : {}}>
      {isLoading && <LoadingSpinner />}
      
      {/* Mobile view */}
      {isMobile ? (
        <>
          {/* Mobile Content */}
          <div className="mobile-content px-3 pt-0 pb-3">
            {/* Add button and search bar */}
            <div className="d-flex justify-content-end mt-2 mb-3">
              <button 
                className="btn btn-primary rounded-circle" 
                onClick={() => setIsModalOpen(true)}
                style={{ width: '40px', height: '40px', padding: '0' }}
              >
                <i className="bi bi-plus" style={{ fontSize: '20px' }}></i>
              </button>
            </div>
            <div className="search-bar mb-3">
              <div className="input-group">
                <span className="input-group-text bg-dark border-secondary">
                  <i className="bi bi-search text-muted"></i>
                </span>
                <input 
                  type="text" 
                  className="form-control bg-dark border-secondary text-white" 
                  placeholder="Search by address, zip, or state"
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </div>
            </div>
            
            {/* Mobile Status Filter Dropdown */}
            <div className="status-filter mb-4">
              <select 
                className="form-select bg-dark text-white border-secondary" 
                value={statusFilter}
                onChange={(e) => handleStatusFilterChange(e.target.value)}
                aria-label="Filter properties by status"
              >
                <option value="ALL">All Properties</option>
                <option value="UNDERWRITING">Underwriting</option>
                <option value="UNDER_CONTRACT">Under Contract</option>
                <option value="UNDER_MANAGEMENT">Under Management</option>
                <option value="UNQUALIFIED">Unqualified</option>
                <option value="DIVESTED">Divested</option>
              </select>
            </div>
            
            {/* Property Sections for Mobile */}
            {!isLoading && filteredProperties.length === 0 ? (
              <div className="text-center my-5">
                <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" fill="currentColor" className="bi bi-house text-muted mb-4" viewBox="0 0 16 16">
                  <path d="M8.707 1.5a1 1 0 0 0-1.414 0L.646 8.146a.5.5 0 0 0 .708.708L2 8.207V13.5A1.5 1.5 0 0 0 3.5 15h9a1.5 1.5 0 0 0 1.5-1.5V8.207l.646-.647a.5.5 0 0 0 .708-.708L13 5.793V2.5a.5.5 0 0 0-.5-.5h-1a.5.5 0 0 0-.5.5v1.293zM13 7.207V13.5a.5.5 0 0 1-.5.5h-9a.5.5 0 0 1-.5-.5V7.207l5-5z"/>
                </svg>
                <p className="text-muted mb-2">You haven't added any properties yet.</p>
                <p className="text-muted">Click the Add button above to get started!</p>
              </div>
            ) : (
              <>
                <PropertySection status="UNDERWRITING" title={HOUSE_STATUSES.UNDERWRITING} />
                <PropertySection status="UNDER_CONTRACT" title={HOUSE_STATUSES.UNDER_CONTRACT} />
                <PropertySection status="UNDER_MANAGEMENT" title={HOUSE_STATUSES.UNDER_MANAGEMENT} />
                <PropertySection status="UNQUALIFIED" title={HOUSE_STATUSES.UNQUALIFIED} />
                <PropertySection status="DIVESTED" title={HOUSE_STATUSES.DIVESTED} />
              </>
            )}
          </div>
        </>
      ) : (
        /* Desktop view */
        <>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2 className="mb-0 text-light">Properties</h2>
            <div className="col-auto d-flex gap-3">
              <button 
                className="btn btn-primary" 
                onClick={() => setIsModalOpen(true)}
              >
                + Add Property
              </button>
            </div>
          </div>
          
          {/* Desktop Status Filter */}
          <div className="status-filter mb-4">
            <div className="d-flex flex-wrap" style={{ gap: '8px' }}>
              <button 
                className={`btn btn-sm ${statusFilter === 'ALL' ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => handleStatusFilterChange('ALL')}
              >
                All
              </button>
              <button 
                className={`btn btn-sm ${statusFilter === 'UNDERWRITING' ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => handleStatusFilterChange('UNDERWRITING')}
              >
                Underwriting
              </button>
              <button 
                className={`btn btn-sm ${statusFilter === 'UNDER_CONTRACT' ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => handleStatusFilterChange('UNDER_CONTRACT')}
              >
                Under Contract
              </button>
              <button 
                className={`btn btn-sm ${statusFilter === 'UNDER_MANAGEMENT' ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => handleStatusFilterChange('UNDER_MANAGEMENT')}
              >
                Under Management
              </button>
              <button 
                className={`btn btn-sm ${statusFilter === 'UNQUALIFIED' ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => handleStatusFilterChange('UNQUALIFIED')}
              >
                Unqualified
              </button>
              <button 
                className={`btn btn-sm ${statusFilter === 'DIVESTED' ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => handleStatusFilterChange('DIVESTED')}
              >
                Divested
              </button>
            </div>
          </div>
          
          {/* Desktop Property Sections */}
          {!isLoading && (
            filteredProperties.length === 0 ? (
              <div className="text-center my-5">
                <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" fill="currentColor" className="bi bi-house text-muted mb-4" viewBox="0 0 16 16">
                  <path d="M8.707 1.5a1 1 0 0 0-1.414 0L.646 8.146a.5.5 0 0 0 .708.708L2 8.207V13.5A1.5 1.5 0 0 0 3.5 15h9a1.5 1.5 0 0 0 1.5-1.5V8.207l.646-.647a.5.5 0 0 0 .708-.708L13 5.793V2.5a.5.5 0 0 0-.5-.5h-1a.5.5 0 0 0-.5.5v1.293zM13 7.207V13.5a.5.5 0 0 1-.5.5h-9a.5.5 0 0 1-.5-.5V7.207l5-5z"/>
                </svg>
                <p className="text-muted mb-2">You haven't added any properties yet.</p>
                <p className="text-muted">Click the Add button above to get started!</p>
              </div>
            ) : (
              <>
                <PropertySection status="UNDERWRITING" title={HOUSE_STATUSES.UNDERWRITING} />
                <PropertySection status="UNDER_CONTRACT" title={HOUSE_STATUSES.UNDER_CONTRACT} />
                <PropertySection status="UNDER_MANAGEMENT" title={HOUSE_STATUSES.UNDER_MANAGEMENT} />
                <PropertySection status="UNQUALIFIED" title={HOUSE_STATUSES.UNQUALIFIED} />
                <PropertySection status="DIVESTED" title={HOUSE_STATUSES.DIVESTED} />
              </>
            )
          )}
        </>
      )}



      <AddHouseModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onAdd={handleAddProperty}
      />
    </div>
  );
};

export default PropertyList;
